import { useInfiniteQuery } from '@tanstack/react-query';
import { Grid, useGrid } from '@virtual-grid/react';
import classNames from 'classnames';
import type { InferGetStaticPropsType } from 'next';
import Head from 'next/head';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';

import AppSidebarLayout from '@/components/AppSidebarLayout';
import AdvancedFilterChips from '@/components/JobBoard/AdvancedFiltersChips';
import Filters from '@/components/JobBoard/Filters/Filters';
import Offer from '@/components/JobBoard/Offer';
import SelectResumeDialog from '@/components/JobBoard/SelectResumeDialog';
import Ordering from '@/components/Orderings';
import Text from '@/components/Text';
import { useSidebar } from '@/components/ui/sidebar';
import {
  Ordering as OrderingType,
  orderingOptions,
  useJobBoardParams,
} from '@/hooks/JobBoardFilters/useJobBoardParams';
import { useGoogleOneTap } from '@/hooks/useGoogleOneTap';
import { useWindowResizeListener } from '@/hooks/useWindowResizeListener';
import { queries } from '@/queries';
import { EnumAccountType } from '@/services/auth/auth';
import { getExternalOffers } from '@/services/external-offers';
import { createMetaTags } from '@/utils/seo';
import { getBreakpoints } from '@/utils/tailwind';

const CandidateJobBoard = ({
  firstOffersPage,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  useGoogleOneTap(EnumAccountType.CANDIDATE);

  const {
    setParams,
    orderingOption,
    params: {
      location: locationParam,
      search: searchParam,
      workType,
      seniorityLevel,
      salaryLower,
      salaryUpper,
      ordering,
      company,
      industry: industryParam,
    },
  } = useJobBoardParams();

  const [initialized, setInitialized] = useState(false);

  const { width } = useWindowResizeListener();
  const { state: sidebarState } = useSidebar();

  const {
    data,
    isPreviousData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    ...queries.externalOffers.list({
      location: locationParam,
      search: searchParam,
      workType,
      seniorityLevel,
      salaryLower: salaryLower?.toString(),
      salaryUpper: salaryUpper?.toString(),
      ordering,
      companyName: company?.join(','),
      industry: industryParam,
    }),
    getNextPageParam: (lastPage, allPages) =>
      lastPage.next ? allPages.length : undefined,
    initialData:
      (!company || company.length <= 0) &&
      !(
        (ordering ?? OrderingType['-CREATED_AT']) !==
        OrderingType['-CREATED_AT']
      ) &&
      !searchParam &&
      !locationParam &&
      !salaryLower &&
      !salaryUpper &&
      (workType?.length ?? 0) <= 0 &&
      (seniorityLevel?.length ?? 0) <= 0 &&
      !industryParam
        ? { pageParams: [undefined, 1, 2], pages: firstOffersPage }
        : undefined,
    staleTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const allRows = data ? data.pages.flatMap((d) => d.results) : [];

  const isMobile = width < (getBreakpoints()?.lg ?? 1024);

  const paddingX = isMobile ? 20 : 64;
  // SIDEBAR_WIDTH / SIDEBAR_WIDTH_ICON from ui/sidebar (16rem / 3rem)
  const sidebarWidth = sidebarState === 'expanded' ? 256 : 48;

  const selectResumeContainerStyles = isMobile
    ? { marginLeft: paddingX, paddingLeft: 0, maxWidth: width - paddingX * 2 }
    : {
        marginLeft: 0,
        paddingLeft: 24,
        maxWidth: 384,
      };
  const grid = useGrid({
    count: allRows.length,
    gap: 20,
    columns: 'auto',
    size: { height: 170, width: isMobile ? 220 : 400 },
    padding: {
      right: paddingX,
      left: isMobile ? paddingX : undefined,
    },
    width: isMobile
      ? undefined
      : width -
        sidebarWidth -
        (selectResumeContainerStyles.maxWidth +
          selectResumeContainerStyles.marginLeft +
          selectResumeContainerStyles.paddingLeft),
    onLoadMore:
      isFetchingNextPage || !hasNextPage ? undefined : () => fetchNextPage(),
    rowVirtualizerInitialRect: { width: 0, height: 1000 },
    columnVirtualizerInitialRect: { width: 1000, height: 0 },
    coreGridInitialRect: { height: 0, width: 2000 },
    loadMoreSize: 505,
    overscan: isMobile ? 4 : 1,
  });

  useEffect(() => {
    setInitialized(true);
  }, []);

  return (
    <>
      <style jsx global>
        {`
          body {
            background: #ffffff;
          }
        `}
      </style>
      <Head>{createMetaTags({ title: 'MindPal - Jobs' })}</Head>
      <div className="flex min-h-screen flex-1 ">
        <div className="relative flex flex-1 flex-col bg-gradient-to-br from-jb-secondary-100 via-jb-primary-100 to-secondary-100">
          <div className="z-10 flex w-full flex-col items-end justify-center gap-3 border-b bg-jb-neutral-100 px-5 lg:sticky lg:top-0 lg:items-center lg:px-16 lg:py-4">
            <Filters />
            <AdvancedFilterChips />
          </div>
          <div
            className={classNames(
              'scroll-transparent pt-6 opacity-100 transition-opacity flex-1 flex-col',
              {
                '!opacity-0': !initialized,
              }
            )}
          >
            <div className="flex flex-col justify-between gap-6 px-5 lg:flex-row lg:px-16 lg:pb-5">
              <Text
                className="text-sm !font-semibold text-[#636363] md:text-body"
                variant="body"
              >
                Showing {data?.pages?.[0].count} Jobs
              </Text>
              <Ordering
                onChange={(order) => setParams({ ordering: order.value })}
                options={orderingOptions}
                value={orderingOption}
              />
            </div>
            <div className="flex flex-col gap-4 lg:flex-row">
              <div
                className="h-fit w-full pt-1 lg:sticky lg:left-0 lg:top-36"
                style={selectResumeContainerStyles}
              >
                <SelectResumeDialog />
              </div>
              <Grid grid={grid}>
                {(i) => {
                  const element = allRows[i];
                  if (!element) return null;
                  return <Offer {...element} isPreviousData={isPreviousData} />;
                }}
              </Grid>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CandidateJobBoard;
export const getStaticProps = async () => {
  const firstOffersPage = await Promise.all([
    getExternalOffers({ limit: 20, offset: 0 }),
    getExternalOffers({ offset: 20, limit: 20 }),
  ]);

  return { props: { firstOffersPage }, revalidate: 30 };
};

CandidateJobBoard.getLayout = (page: ReactElement) => (
  <AppSidebarLayout position="fixed">{page}</AppSidebarLayout>
);
